import React from "react";
import FieldCard from "./FieldCard";
import InputField from "./InputField";

function RoomCard({ title, children }) {
  return (
    <div className="rounded-xl border border-gray-700 bg-gray-900 p-4">
      <div className="mb-3 text-sm font-semibold text-white">{title}</div>
      {children}
    </div>
  );
}

function createDefaultRoom(base = {}) {
  return {
    id: crypto.randomUUID(),
    name: "",
    floorAreaM2: "",
    flooringType: "laminate",
    wastePercent: 8,
    oldFloorRemovalRequired: false,
    subfloorPrepRequired: false,
    underlayIncluded: true,
    skirtingIncluded: true,
    ...base,
  };
}

function normalizeRoomForEditor(room = {}) {
  return createDefaultRoom({
    ...room,
    id: room.id || crypto.randomUUID(),
    flooringType: room.flooringType || "laminate",
    wastePercent:
      room.wastePercent === "" ? "" : Number(room.wastePercent ?? 8) || 8,
    oldFloorRemovalRequired: !!room.oldFloorRemovalRequired,
    subfloorPrepRequired: !!room.subfloorPrepRequired,
    underlayIncluded:
      room.underlayIncluded !== undefined ? !!room.underlayIncluded : true,
    skirtingIncluded:
      room.skirtingIncluded !== undefined ? !!room.skirtingIncluded : true,
  });
}

export default function FlooringEditor({
  parsedData,
  setParsedData,
  errors = {},
}) {
  const rooms =
    Array.isArray(parsedData?.rooms) && parsedData.rooms.length
      ? parsedData.rooms.map(normalizeRoomForEditor)
      : [
          normalizeRoomForEditor({
            name: "",
            floorAreaM2: parsedData.floorAreaM2 ?? "",
            flooringType: parsedData.flooringType || "laminate",
            wastePercent: parsedData.wastePercent ?? 8,
            oldFloorRemovalRequired: !!parsedData.oldFloorRemovalRequired,
            subfloorPrepRequired: !!parsedData.subfloorPrepRequired,
            underlayIncluded:
              parsedData.underlayIncluded !== undefined
                ? !!parsedData.underlayIncluded
                : true,
            skirtingIncluded:
              parsedData.skirtingIncluded !== undefined
                ? !!parsedData.skirtingIncluded
                : true,
          }),
        ];

  const setRooms = (nextRooms) => {
    const normalizedRooms = (Array.isArray(nextRooms) ? nextRooms : []).map(
      normalizeRoomForEditor
    );

    setParsedData((prev) => ({
      ...prev,
      projectType: "flooring",
      primaryProjectType: "flooring",
      projectTypes: ["flooring"],
      rooms: normalizedRooms,
      floorAreaM2: normalizedRooms[0]?.floorAreaM2 ?? "",
      flooringType: normalizedRooms[0]?.flooringType || "laminate",
      wastePercent: normalizedRooms[0]?.wastePercent ?? 8,
      oldFloorRemovalRequired: !!normalizedRooms[0]?.oldFloorRemovalRequired,
      subfloorPrepRequired: !!normalizedRooms[0]?.subfloorPrepRequired,
      underlayIncluded:
        normalizedRooms[0]?.underlayIncluded !== undefined
          ? !!normalizedRooms[0]?.underlayIncluded
          : true,
      skirtingIncluded:
        normalizedRooms[0]?.skirtingIncluded !== undefined
          ? !!normalizedRooms[0]?.skirtingIncluded
          : true,
    }));
  };

  const addRoom = () => {
    setRooms([...rooms, createDefaultRoom()]);
  };

  const updateRoom = (index, field, value) => {
    setRooms(
      rooms.map((room, i) =>
        i === index ? normalizeRoomForEditor({ ...room, [field]: value }) : room
      )
    );
  };

  const removeRoom = (index) => {
    const nextRooms = rooms.filter((_, i) => i !== index);

    setRooms(nextRooms.length ? nextRooms : [createDefaultRoom()]);
  };

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-gray-700 bg-gray-900 px-4 py-3 text-sm text-gray-400">
        Define one or more floor areas or rooms. Enter area, flooring type, and
        installation requirements for each room separately.
      </div>

      <div className="flex flex-wrap gap-3">
        <button
          type="button"
          onClick={addRoom}
          className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
        >
          Add room
        </button>
      </div>

      <div className="space-y-4">
        {rooms.map((room, index) => (
          <RoomCard
            key={room.id || `room-${index}`}
            title={room.name?.trim() || `Room ${index + 1}`}
          >
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <FieldCard
                label="Room name"
                warning="Optional internal name like Hallway, Kitchen, Bedroom 1."
              >
                <InputField
                  value={room.name ?? ""}
                  onChange={(e) => updateRoom(index, "name", e.target.value)}
                  placeholder="e.g. Kitchen"
                />
              </FieldCard>

              <FieldCard
                label="Floor area"
                unit="m²"
                error={
                  errors[`rooms.${index}`] || (index === 0 ? errors.floorAreaM2 : "")
                }
                warning="Enter the total surface to be covered for this room."
              >
                <InputField
                  type="number"
                  value={room.floorAreaM2 ?? ""}
                  onChange={(e) =>
                    updateRoom(
                      index,
                      "floorAreaM2",
                      e.target.value === "" ? "" : Number(e.target.value)
                    )
                  }
                  placeholder="e.g. 42"
                />
              </FieldCard>

              <FieldCard
                label="Flooring type"
                warning="Choose the material the customer wants installed."
              >
                <select
                  value={room.flooringType || "laminate"}
                  onChange={(e) =>
                    updateRoom(index, "flooringType", e.target.value)
                  }
                  className="h-10 w-full rounded-lg border border-gray-600 bg-gray-800 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="laminate">Laminate</option>
                  <option value="wood">Wood</option>
                  <option value="vinyl">Vinyl</option>
                  <option value="tile">Tile</option>
                  <option value="carpet">Carpet</option>
                  <option value="other">Other</option>
                </select>
              </FieldCard>

              <FieldCard
                label="Waste"
                unit="%"
                warning="Extra material allowance for cutting and waste."
              >
                <InputField
                  type="number"
                  value={room.wastePercent ?? 8}
                  onChange={(e) =>
                    updateRoom(
                      index,
                      "wastePercent",
                      e.target.value === "" ? "" : Number(e.target.value)
                    )
                  }
                  placeholder="e.g. 8"
                />
              </FieldCard>

              {index === 0 && (
                <FieldCard
                  label="Finish level"
                  warning="Use premium if the project requires a higher finish standard."
                >
                  <select
                    value={parsedData.finishLevel || "standard"}
                    onChange={(e) =>
                      setParsedData((prev) => ({
                        ...prev,
                        projectType: "flooring",
                        primaryProjectType: "flooring",
                        projectTypes: ["flooring"],
                        finishLevel: e.target.value,
                        rooms,
                      }))
                    }
                    className="h-10 w-full rounded-lg border border-gray-600 bg-gray-800 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="standard">Standard</option>
                    <option value="premium">Premium</option>
                  </select>
                </FieldCard>
              )}

              <FieldCard
                label="Remove old floor?"
                warning="Enable if existing flooring must be removed and disposed of."
              >
                <select
                  value={room.oldFloorRemovalRequired ? "yes" : "no"}
                  onChange={(e) =>
                    updateRoom(
                      index,
                      "oldFloorRemovalRequired",
                      e.target.value === "yes"
                    )
                  }
                  className="h-10 w-full rounded-lg border border-gray-600 bg-gray-800 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </select>
              </FieldCard>

              <FieldCard
                label="Subfloor prep required?"
                warning="Enable if leveling, grinding, or repair is needed before installation."
              >
                <select
                  value={room.subfloorPrepRequired ? "yes" : "no"}
                  onChange={(e) =>
                    updateRoom(
                      index,
                      "subfloorPrepRequired",
                      e.target.value === "yes"
                    )
                  }
                  className="h-10 w-full rounded-lg border border-gray-600 bg-gray-800 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </select>
              </FieldCard>

              <FieldCard
                label="Include underlay?"
                warning="Disable if underlay is not needed or is already included."
              >
                <select
                  value={room.underlayIncluded === false ? "no" : "yes"}
                  onChange={(e) =>
                    updateRoom(index, "underlayIncluded", e.target.value === "yes")
                  }
                  className="h-10 w-full rounded-lg border border-gray-600 bg-gray-800 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </FieldCard>

              <FieldCard
                label="Include skirting?"
                warning="Disable if skirting boards are not part of this room."
              >
                <select
                  value={room.skirtingIncluded === false ? "no" : "yes"}
                  onChange={(e) =>
                    updateRoom(index, "skirtingIncluded", e.target.value === "yes")
                  }
                  className="h-10 w-full rounded-lg border border-gray-600 bg-gray-800 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </FieldCard>
            </div>

            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={() => removeRoom(index)}
                className="rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-700"
              >
                Remove room
              </button>
            </div>
          </RoomCard>
        ))}
      </div>
    </div>
  );
}